import type { MiceChosenFlight, MiceMoney, MiceNeedView } from '@voll-intelligence/domain';

export type MiceOrderDirection = 'OUTWARD' | 'RETURN';
/** Sentidos que o operador tirou da compra, por necessidade. */
export type MicePurchaseSkip = ReadonlyMap<string, ReadonlySet<MiceOrderDirection>>;

export interface PurchaseSummary {
  travelers: number;
  /** Sentidos com voo escolhido: cada um vira um bilhete. */
  tickets: number;
  /** `null` sem bilhete ou com moedas diferentes. */
  total: MiceMoney | null;
  /** Teto aprovado: cada bilhete a tarifa × (1 + tolerância), como o mice-api grava no pedido. */
  ceiling: MiceMoney | null;
  /** Por companhia do primeiro trecho, da maior para a menor soma. */
  byCarrier: { carrier: string; tickets: number; amount: number }[];
}

const cents = (amount: number): number => Math.round(amount * 100) / 100;

const chosenOf = (need: MiceNeedView, skip: ReadonlySet<MiceOrderDirection> | undefined) =>
  [
    skip?.has('OUTWARD') ? null : need.recommendation?.outbound,
    skip?.has('RETURN') ? null : need.recommendation?.inbound,
  ].flatMap((f): MiceChosenFlight[] => (f ? [f] : []));

export function summarizePurchase(
  needs: readonly MiceNeedView[],
  skip: MicePurchaseSkip = new Map(),
  tolerance = 0,
): PurchaseSummary {
  const flights = needs.flatMap((n) => chosenOf(n, skip.get(n.id)));
  const currency = flights[0]?.farePrice.currency;
  const sameCurrency =
    currency !== undefined && flights.every((f) => f.farePrice.currency === currency);
  const carriers = new Map<string, { tickets: number; amount: number }>();
  for (const f of flights) {
    const carrier = f.hops[0]?.carrier ?? '—';
    const acc = carriers.get(carrier) ?? { tickets: 0, amount: 0 };
    carriers.set(carrier, { tickets: acc.tickets + 1, amount: acc.amount + f.farePrice.amount });
  }
  const sum = (amount: (f: MiceChosenFlight) => number): number =>
    cents(flights.reduce((acc, f) => acc + amount(f), 0));
  return {
    travelers: needs.length,
    tickets: flights.length,
    total: sameCurrency ? { amount: sum((f) => f.farePrice.amount), currency } : null,
    ceiling: sameCurrency
      ? { amount: sum((f) => cents(f.farePrice.amount * (1 + tolerance))), currency }
      : null,
    byCarrier: [...carriers.entries()]
      .map(([carrier, v]) => ({ carrier, ...v }))
      .sort((a, b) => b.amount - a.amount),
  };
}
